import { DeepDiveTile } from '@/components/DeepDiveTile';
import { Skeleton, SkeletonList } from '@/components/Skeleton';
import { SyncBadge } from '@/components/SyncBadge';
import { Table } from '@/components/Table';
import { WindowSwitcher } from '@/components/WindowSwitcher';
import { data } from '@/lib/data';
import { h } from '@/lib/dom';
import { formatDate, formatNumber, sliceRecent, WINDOW_LABELS, windowDays } from '@/lib/format';
import type { WindowKey } from '@/lib/format';
import { currentSession } from '@/lib/session';
import type { RouteContext } from '@/router';
import { navigate } from '@/router';
import { errorRetry } from '@/lib/states';
import type {
  HydrationDayPoint,
  HydrationDetailPayload,
  IntegrationSummary,
  MealTypeKey,
  NutritionDetail,
  NutritionDayPoint,
  NutritionHourPoint,
  NutritionTopMeal,
} from '@/api/types';
import { pageLayout } from './layout';

type NutritionWindowKey = WindowKey;

type HourMetricKey = 'kcal' | 'protein' | 'fat_total' | 'fat_saturated' | 'carbs';
type DayMetricKey = 'kcal' | 'protein' | 'fat' | 'fat_saturated' | 'carbs';

const HOUR_METRIC_LABELS: Record<HourMetricKey, string> = {
  kcal: 'Ккал',
  protein: 'Белок',
  fat_total: 'Жиры',
  fat_saturated: 'Насыщ.',
  carbs: 'Углеводы',
};

const HOUR_METRIC_UNITS: Record<HourMetricKey, string> = {
  kcal: 'ккал',
  protein: 'г',
  fat_total: 'г',
  fat_saturated: 'г',
  carbs: 'г',
};

const DAY_METRIC_LABELS: Record<DayMetricKey, string> = {
  kcal: 'Ккал',
  protein: 'Белок',
  fat: 'Жиры',
  fat_saturated: 'Насыщ.',
  carbs: 'Углеводы',
};

const DAY_METRIC_UNITS: Record<DayMetricKey, string> = {
  kcal: 'ккал',
  protein: 'г',
  fat: 'г',
  fat_saturated: 'г',
  carbs: 'г',
};

const MEAL_TYPE_LABELS: Record<MealTypeKey, string> = {
  breakfast: 'Завтрак (05–10)',
  lunch: 'Обед (11–15)',
  dinner: 'Ужин (16–21)',
  snack: 'Перекус / ночь',
};

const WINDOWS: NutritionWindowKey[] = ['7d', '30d', '90d'];

export async function renderNutrition(_ctx: RouteContext): Promise<Node> {
  const user = currentSession();
  if (!user) {
    navigate('/login', { replace: true });
    return h('div');
  }

  let currentWindow: NutritionWindowKey = '30d';
  let detail: NutritionDetail | null = null;
  let hydration: IntegrationSummary<HydrationDetailPayload> | null = null;

  const content = h('div', { id: 'nutrition-content', class: 'nutrition-page' }, [
    h('div', { class: 'flex flex-col gap-6' }, [
      Skeleton({ variant: 'rect', height: '72px' }),
      SkeletonList(3),
      Skeleton({ variant: 'chart', height: '160px' }),
    ]),
  ]);

  const root = pageLayout({
    user,
    active: 'nutrition',
    children: [content],
  });

  const onWindowChange = (w: NutritionWindowKey) => { currentWindow = w; render(); };

  const render = () => {
    if (!detail) return;
    content.innerHTML = '';
    content.append(...buildSections(detail, hydration, currentWindow, onWindowChange));
  };

  void (async () => {
    try {
      // Hydration is best-effort: a failure there must not break the page.
      const [nut, hyd] = await Promise.all([
        data.getNutritionDetail(),
        data.getHydrationDetail().catch(() => null),
      ]);
      detail = nut;
      hydration = hyd;
      render();
    } catch (err) {
      content.innerHTML = '';
      content.appendChild(errorRetry(err, 'Не удалось загрузить данные питания.', '/nutrition'));
    }
  })();

  return root;
}

function buildSections(
  detail: NutritionDetail,
  hydration: IntegrationSummary<HydrationDetailPayload> | null,
  window: NutritionWindowKey,
  onWindowChange: (w: NutritionWindowKey) => void,
): Node[] {
  const header = nutritionHeader(detail, window, onWindowChange);
  const hydrationEl = hydrationSection(hydration, window);

  if (detail.message && allEmpty(detail)) {
    return [
      header,
      h('div', { class: 'empty-state' }, detail.message),
      ...(hydrationEl ? [hydrationEl] : []),
    ];
  }

  return [
    header,
    tilesSection(detail, window),
    mealTypeSection(detail, window),
    hourlySection(detail, window),
    recentSection(detail, window),
    topMealsSection(detail, window),
    ...(hydrationEl ? [hydrationEl] : []),  // «Гидратация» — последней на странице
  ];
}

function nutritionHeader(
  detail: NutritionDetail,
  window: NutritionWindowKey,
  onWindowChange: (w: NutritionWindowKey) => void,
): HTMLElement {
  return h('header', { class: 'nutrition-header' }, [
    h('div', { class: 'nutrition-titles' }, [
      h('h1', { class: 'nutrition-title' }, '🍗 Питание'),
      h('p', { class: 'nutrition-meta' }, metaLine(detail, window)),
    ]),
    h('div', { class: 'nutrition-header-controls' }, [
      h('div', { class: 'nutrition-window-switcher' },
        WindowSwitcher({
          current: window,
          windows: WINDOWS,
          labelFor: (w) => WINDOW_LABELS[w],
          onChange: onWindowChange,
        })),
      h('div', { class: 'nutrition-sync' }, SyncBadge(detail)),
    ]),
  ]);
}

function allEmpty(detail: NutritionDetail): boolean {
  return Object.values(detail.windows).every((w) => w.days_with_data === 0);
}

// ---------- Tiles section ----------

function tilesSection(detail: NutritionDetail, window: NutritionWindowKey): HTMLElement {
  const w = detail.windows[window];
  const tiles: Array<[string, string, string]> = [
    ['Калории', fmtInt(w.avg_kcal), 'ккал'],
    ['Белок', fmtInt(w.avg_protein_g), 'г'],
    ['Жиры', fmtDec(w.avg_fat_total_g), 'г'],
    ['Насыщ. жиры', fmtDec(w.avg_fat_saturated_g), 'г'],
    ['Углеводы', fmtInt(w.avg_carbs_g), 'г'],
    ['Клетчатка', fmtDec(w.avg_fiber_g), 'г'],
  ];

  return h('section', { class: 'nutrition-section', 'data-section': 'tiles' }, [
    h('h2', { class: 'nutrition-section-header' }, `Средние за ${WINDOW_LABELS[window].toLowerCase()}`),
    h('div', { class: 'deepdive-stat-grid' },
      tiles.map(([label, value, unit]) => DeepDiveTile(label, value, unit))),
  ]);
}

function metaLine(detail: NutritionDetail, window: NutritionWindowKey): string {
  if (!detail.fetched_at) return 'Данные отсутствуют';
  const w = detail.windows[window];
  const total = windowDays(window);
  const skipped = w.days_skipped > 0 ? ` · ${w.days_skipped} пропущено` : '';
  return `Окно ${WINDOW_LABELS[window].toLowerCase()} · ${w.days_with_data} из ${total} дней с логом${skipped}`;
}

// ---------- Meal type section ----------

function mealTypeSection(detail: NutritionDetail, window: NutritionWindowKey): HTMLElement {
  const byMealType = detail.by_meal_type[window] ?? detail.by_meal_type['30d'] ?? {};
  const categories: MealTypeKey[] = ['breakfast', 'lunch', 'dinner', 'snack'];

  type Row = {
    label: string;
    avg_kcal: string;
    avg_protein_g: string;
    avg_carbs_g: string;
    avg_fat_g: string;
    avg_fat_saturated_g: string;
    count: string;
  };

  const rows: Row[] = categories.map((cat) => {
    const b = byMealType[cat];
    if (!b) {
      return { label: MEAL_TYPE_LABELS[cat], avg_kcal: '—', avg_protein_g: '—', avg_carbs_g: '—', avg_fat_g: '—', avg_fat_saturated_g: '—', count: '0' };
    }
    return {
      label: MEAL_TYPE_LABELS[cat],
      avg_kcal: fmtInt(b.avg_kcal),
      avg_protein_g: fmtDec(b.avg_protein_g),
      avg_carbs_g: fmtDec(b.avg_carbs_g),
      avg_fat_g: fmtDec(b.avg_fat_g),
      avg_fat_saturated_g: fmtDec(b.avg_fat_saturated_g),
      count: String(b.count),
    };
  });

  const table = Table<Row>({
    ariaLabel: `Средние по категории приёма (${WINDOW_LABELS[window]})`,
    empty: 'Нет данных.',
    columns: [
      { key: 'label', header: 'Категория' },
      { key: 'avg_kcal', header: 'Ккал', align: 'right' },
      { key: 'avg_protein_g', header: 'Белок, г', align: 'right' },
      { key: 'avg_carbs_g', header: 'Углеводы, г', align: 'right' },
      { key: 'avg_fat_g', header: 'Жиры, г', align: 'right' },
      { key: 'avg_fat_saturated_g', header: 'Насыщ., г', align: 'right' },
      { key: 'count', header: 'Приёмов', align: 'right' },
    ],
    rows,
  });

  return h('section', { class: 'nutrition-section' }, [
    h('h2', { class: 'nutrition-section-header' }, 'По категории приёма'),
    h('p', { class: 'nutrition-section-hint' },
      'Категория определяется по времени приёма (не по пометке в логе): утро 05–10, обед 11–15, ужин 16–21, перекус / ночь — всё остальное.'),
    h('div', { class: 'data-table-scroll' }, table),
  ]);
}

// ---------- Hourly section ----------

const HOUR_METRICS: HourMetricKey[] = ['kcal', 'protein', 'fat_total', 'fat_saturated', 'carbs'];

function getHourValue(p: NutritionHourPoint, metric: HourMetricKey): number {
  switch (metric) {
    case 'kcal': return p.avg_kcal_at_this_hour;
    case 'protein': return p.avg_protein_g_at_this_hour;
    case 'fat_total': return p.avg_fat_total_g_at_this_hour;
    case 'fat_saturated': return p.avg_fat_saturated_g_at_this_hour;
    case 'carbs': return p.avg_carbs_g_at_this_hour;
  }
}

function hourlySection(detail: NutritionDetail, window: NutritionWindowKey): HTMLElement {
  let currentMetric: HourMetricKey = 'kcal';

  const chart = renderBarChart<NutritionHourPoint>({
    chartAreaClass: 'nutrition-hour-chart',
    chartWrapClass: 'nutrition-hour-chart-wrap',
    ariaLabel: 'Гистограмма по часам',
    columnClass: 'nutrition-hour-col',
    barClass: 'nutrition-hour-bar',
    labelClass: 'nutrition-hour-label',
    minMax: 1,
    getValue: (p) => getHourValue(p, currentMetric),
    barAttrs: (p, val) => ({
      aria: { label: `${p.hour}:00 — ${fmtRounded(val ?? 0)} ${HOUR_METRIC_UNITS[currentMetric]}, ${p.meals_logged} приёмов` },
    }),
    labelText: (p) => (p.hour % 3 === 0 ? String(p.hour).padStart(2, '0') : ''),
    tooltipText: (p, val) =>
      `${String(p.hour).padStart(2, '0')}:00 · ${fmtRounded(val ?? 0)} ${HOUR_METRIC_UNITS[currentMetric]} · ${pluralMeals(p.meals_logged)}`,
  });

  const renderBars = () => {
    const points = detail.by_hour[window] ?? detail.by_hour['30d'] ?? [];
    chart.render(points);
  };

  let switcherEl: HTMLElement = h('div');

  function rebuildHourSwitcher(): HTMLElement {
    switcherEl = buildMetricSwitcher(
      HOUR_METRICS,
      currentMetric,
      HOUR_METRIC_LABELS,
      (m) => { currentMetric = m; switcherEl.replaceWith(rebuildHourSwitcher()); renderBars(); },
    );
    return switcherEl;
  }

  switcherEl = rebuildHourSwitcher();

  const section = h('section', { class: 'nutrition-section' }, [
    h('h2', { class: 'nutrition-section-header' }, 'Распределение по часам суток'),
    switcherEl,
    chart.chartWrap,
  ]);

  // Initial render deferred so DOM is ready
  queueMicrotask(() => renderBars());

  return section;
}

// ---------- Recent days section ----------

const DAY_METRICS: DayMetricKey[] = ['kcal', 'protein', 'fat', 'fat_saturated', 'carbs'];

function getDayValue(d: NutritionDayPoint, metric: DayMetricKey): number | null {
  switch (metric) {
    case 'kcal': return d.kcal;
    case 'protein': return d.protein_g;
    case 'fat': return d.fat_g;
    case 'fat_saturated': return d.fat_saturated_g;
    case 'carbs': return d.carbs_g;
  }
}

function recentSection(detail: NutritionDetail, window: NutritionWindowKey): HTMLElement {
  let currentMetric: DayMetricKey = 'kcal';

  const titleEl = h('h2', { class: 'nutrition-section-header' }, '');

  const chart = renderBarChart<NutritionDayPoint>({
    chartAreaClass: 'nutrition-day-chart',
    chartWrapClass: 'nutrition-day-chart-wrap',
    ariaLabel: 'Калорийность по дням',
    columnClass: 'nutrition-day-col',
    barClass: 'nutrition-day-bar',
    labelClass: 'nutrition-day-label',
    minMax: 1,
    getValue: (d) => getDayValue(d, currentMetric),
    // Skip-day (очищен вручную в Sunny) — своя плашка вместо data-empty.
    barAttrs: (d, val) => (d.skipped
      ? { 'data-skipped': 'true' }
      : { 'data-empty': val == null ? 'true' : undefined }),
    labelText: (d) => d.day.slice(5),
    tooltipText: (d, val) => {
      if (d.skipped) return `${formatDate(d.day)} · день пропущен (очищен)`;
      return val != null
        ? `${formatDate(d.day)} · ${fmtRounded(val)} ${DAY_METRIC_UNITS[currentMetric]}`
        : `${formatDate(d.day)} · нет данных`;
    },
  });

  const renderBars = () => {
    const recent = sliceRecent(detail.recent, window);
    titleEl.textContent = `Последние ${windowDays(window)} дней — ${DAY_METRIC_LABELS[currentMetric].toLowerCase()}`;
    chart.chartArea.setAttribute('aria-label', `${DAY_METRIC_LABELS[currentMetric]} за ${WINDOW_LABELS[window]}`);
    chart.render(recent);
  };

  let switcherEl: HTMLElement = h('div');

  function rebuildDaySwitcher(): HTMLElement {
    switcherEl = buildMetricSwitcher(
      DAY_METRICS,
      currentMetric,
      DAY_METRIC_LABELS,
      (m) => { currentMetric = m; switcherEl.replaceWith(rebuildDaySwitcher()); renderBars(); },
    );
    return switcherEl;
  }

  switcherEl = rebuildDaySwitcher();

  const section = h('section', { class: 'nutrition-section' }, [
    titleEl,
    switcherEl,
    chart.chartWrap,
  ]);

  queueMicrotask(() => renderBars());

  return section;
}

// ---------- Hydration section ----------

function hydrationSection(
  hydration: IntegrationSummary<HydrationDetailPayload> | null,
  window: NutritionWindowKey,
): HTMLElement | null {
  if (!hydration) return null;
  const recent = hydration.payload.recent ?? [];
  if (!recent.some((d) => d.ml != null)) return null;  // no water logged yet → hide

  const windows = hydration.payload.windows ?? null;
  const w = windows?.[window] ?? windows?.['30d'] ?? null;
  const avgL = w?.avg_liters ?? null;
  const daysWith = w?.days_with_data ?? 0;

  const chart = renderBarChart<HydrationDayPoint>({
    chartAreaClass: 'hydration-day-chart',
    chartWrapClass: 'nutrition-day-chart-wrap',
    ariaLabel: 'Вода по дням',
    columnClass: 'hydration-day-col',
    barClass: 'hydration-day-bar',
    labelClass: 'hydration-day-label',
    minMax: 0.5,
    getValue: (d) => d.liters,
    barAttrs: (_d, val) => ({ 'data-empty': val == null ? 'true' : undefined }),
    labelText: (d) => d.day.slice(5),
    tooltipText: (d, val) => (val != null
      ? `${formatDate(d.day)} · ${formatNumber(val, 1)} л`
      : `${formatDate(d.day)} · нет данных`),
  });

  const renderBars = () => {
    chart.render(sliceRecent(recent, window));
  };

  const section = h('section', { class: 'nutrition-section', 'data-section': 'hydration' }, [
    h('h2', { class: 'nutrition-section-header' }, '💧 Гидратация'),
    h('p', { class: 'nutrition-section-hint' },
      `Вода из Apple Health · ${daysWith} из ${windowDays(window)} дней с логом`),
    h('div', { class: 'deepdive-stat-grid' }, [
      DeepDiveTile('Вода', avgL != null ? formatNumber(avgL, 1) : '—', 'л/день'),
    ]),
    h('h3', { class: 'nutrition-section-subheader' }, `Последние ${windowDays(window)} дней — вода`),
    chart.chartWrap,
  ]);

  queueMicrotask(() => renderBars());
  return section;
}

// ---------- Top meals section ----------

function topMealsSection(detail: NutritionDetail, window: NutritionWindowKey): HTMLElement {
  const meals: NutritionTopMeal[] = detail.top_meals[window] ?? detail.top_meals['30d'] ?? [];

  type TopRow = {
    name: string;
    frequency: string;
    avg_kcal: string;
    avg_protein_g: string;
    avg_fat_total_g: string;
    avg_fat_saturated_g: string;
    avg_carbs_g: string;
  };

  const rows: TopRow[] = meals.map((m) => ({
    name: m.name,
    frequency: String(m.frequency),
    avg_kcal: fmtInt(m.avg_kcal),
    avg_protein_g: fmtDec(m.avg_protein_g),
    avg_fat_total_g: fmtDec(m.avg_fat_total_g),
    avg_fat_saturated_g: fmtDec(m.avg_fat_saturated_g),
    avg_carbs_g: fmtDec(m.avg_carbs_g),
  }));

  const table = Table<TopRow>({
    ariaLabel: `Топ блюд по частоте (${WINDOW_LABELS[window]})`,
    empty: `Недостаточно данных за ${WINDOW_LABELS[window].toLowerCase()}.`,
    columns: [
      {
        key: 'name',
        header: 'Блюдо',
        render: (r) => h('div', { class: 'nutrition-meal-name' }, r.name),
      },
      { key: 'frequency', header: 'Раз', align: 'right', render: (r) => r.frequency },
      { key: 'avg_kcal', header: 'Ккал', align: 'right', render: (r) => r.avg_kcal },
      { key: 'avg_protein_g', header: 'Белок, г', align: 'right', render: (r) => r.avg_protein_g },
      { key: 'avg_fat_total_g', header: 'Жиры, г', align: 'right', render: (r) => r.avg_fat_total_g },
      { key: 'avg_fat_saturated_g', header: 'Насыщ., г', align: 'right', render: (r) => r.avg_fat_saturated_g },
      { key: 'avg_carbs_g', header: 'Углеводы, г', align: 'right', render: (r) => r.avg_carbs_g },
    ],
    rows,
  });

  return h('section', { class: 'nutrition-section' }, [
    h('h2', { class: 'nutrition-section-header' }, 'Топ-10 блюд'),
    h('div', { class: 'data-table-scroll' }, table),
  ]);
}

// ---------- Shared helpers ----------

interface BarChart<T> {
  chartWrap: HTMLElement;
  chartArea: HTMLElement;
  render: (items: T[]) => void;
}

interface BarChartConfig<T> {
  chartAreaClass: string;
  chartWrapClass: string;
  ariaLabel: string;
  columnClass: string;
  barClass: string;
  labelClass: string;
  minMax: number;
  getValue: (item: T) => number | null;
  barAttrs: (item: T, value: number | null) => Record<string, unknown>;
  labelText: (item: T) => string;
  tooltipText: (item: T, value: number | null) => string;
}

// Shared hover-bar column chart: owns the column DOM, mouseenter/mouseleave
// tooltip and its positioning math. Per-section shaping (value extraction,
// aria-label vs data-empty on the bar, label/tooltip text) lives in the config.
function renderBarChart<T>(config: BarChartConfig<T>): BarChart<T> {
  const chartArea = h('div', {
    class: config.chartAreaClass,
    role: 'img',
    aria: { label: config.ariaLabel },
  });
  const tooltip = h('div', { class: 'chart-tooltip' });
  const chartWrap = h('div', { class: config.chartWrapClass }, [chartArea, tooltip]);

  const render = (items: T[]) => {
    const maxVal = Math.max(config.minMax, ...items.map((item) => config.getValue(item) ?? 0));
    chartArea.innerHTML = '';

    items.forEach((item) => {
      const val = config.getValue(item);
      const heightPct = val != null ? Math.round((val / maxVal) * 100) : 0;

      const col = h('div', { class: config.columnClass }, [
        h('div', {
          class: config.barClass,
          style: `height:${heightPct}%;`,
          ...config.barAttrs(item, val),
        }),
        h('div', { class: config.labelClass }, config.labelText(item)),
      ]);

      col.addEventListener('mouseenter', (ev) => {
        tooltip.textContent = config.tooltipText(item, val);
        tooltip.classList.add('chart-tooltip--visible');
        const rect = (ev.currentTarget as HTMLElement).getBoundingClientRect();
        const wrapRect = chartWrap.getBoundingClientRect();
        const left = rect.left - wrapRect.left + rect.width / 2;
        tooltip.style.left = `${Math.max(4, Math.min(wrapRect.width - 160, left - 80))}px`;
        tooltip.style.top = '4px';
      });
      col.addEventListener('mouseleave', () => {
        tooltip.classList.remove('chart-tooltip--visible');
      });

      chartArea.appendChild(col);
    });
  };

  return { chartWrap, chartArea, render };
}

function buildMetricSwitcher<K extends string>(
  metrics: readonly K[],
  current: K,
  labels: Record<K, string>,
  onChange: (k: K) => void,
): HTMLElement {
  return h('div', { class: 'nutrition-metric-switcher' },
    WindowSwitcher({
      current,
      windows: metrics,
      labelFor: (k) => labels[k],
      onChange,
    }));
}

function fmtInt(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return Math.round(v).toLocaleString('ru-RU');
}

function fmtRounded(v: number): string {
  return Math.round(v).toLocaleString('ru-RU');
}

function fmtDec(v: number | null | undefined): string {
  return formatNumber(v, 1);
}

function pluralMeals(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 19) return `${n} приёмов`;
  if (mod10 === 1) return `${n} приём`;
  if (mod10 >= 2 && mod10 <= 4) return `${n} приёма`;
  return `${n} приёмов`;
}

